import {
  Box,
  Button,
  IconButton,
  Link,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  convertESTtoUserLocalDateAndTime,
  debounceEventHandler,
  getFormattedStatsCount,
  handleErrorMessage,
  initialPageFilterState,
  isTruthy,
  openSuccessNotification,
  validatePageQuery,
  validatePerPageQuery,
} from "helpers/methods";
import CenteredAuthenticatedLayout from "layouts/CenteredAuthenticated/CenteredAuthenticatedLayout";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import history from "utils/history";
import CenteredCardLayout from "layouts/CenteredTableView/CenteredCardLayout";
import strings from "global/constants/StringConstants";
import SearchIcon from "assets/icons1/search_icon.svg";
import DeleteIcon from "assets/icons1/DeleteIcon.svg";
import ExportIcon from "assets/icons1/export_csv_icon.svg";
import CustomTable from "global/components/CustomTable/CustomTable";
import { contactGroupTableHeader } from "../GropsConstants";
import viewpaths from "global/constants/ViewPathConstants";
import FiberManualRecordRoundedIcon from "@mui/icons-material/FiberManualRecordRounded";
import {
  deleteContactGroup,
  deleteContactSingleGroup,
  exportGroup,
  getContactsBySegmentId,
  getContactsCountBySegmentId,
} from "../GroupsService";
import { useLocation } from "react-router-dom";
import remove_icon from "../../../assets/icons1/remove_icon.svg";
import ConfirmationModal from "screens/Templates/Components/ConfirmationModal";
import UpdateGroupModal from "./UpdateGroupModal";
import { RouterLink } from "components/router-link";
import { KeyboardBackspace } from "@mui/icons-material";
import CustomLoader from "global/components/CustomLoader/CustomLoader";
import { hasAccessTo } from "layouts/Config";
import AddContactsModal from "screens/Contacts/AddContacts/AddContactsModal";
import { pageFilters } from "models/interfaces";
import CustomInfoTooltip from "global/components/CustomInfoTooltip/CustomInfoTooltip";

const getColors = {
  0: "#475569",
  1: "#47AA12",
} as { [key: number]: string };

export const getGroupStatus = (status: number) => {
  if (status === 0) {
    return "InActive";
  }
  if (status === 1) {
    return "Active";
  }
};

const ViewGroup = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation<{ group?: any; subordinates: number[] }>();
  const group = location.state?.group;
  const subordinates = location.state?.subordinates;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isApiLoading, setIsApiLoading] = useState<boolean>(false);
  const [contacts, setContacts] = useState<string[]>([]);
  const [count, setCount] = useState<number>();
  const [isDeleteGroupModal, setIsDeleteGroupModal] = useState<boolean>(false);
  const [isUpdateGroupModal, setIsUpdateGroupModal] = useState<boolean>(false);
  const [isDeleteSingleContactModal, setIsDeleteSingleContactModal] =
    useState<boolean>(false);
  const [addContactsModal, setAddContactsModal] = useState<boolean>(false);
  const [selectedSingleContact, setSelectedSingleContact] = useState<any>();
  const searchTerm = useRef<any>(null);
  const urlParams = new URLSearchParams(useLocation().search);
  const pageFromUrl = validatePageQuery(urlParams.get("page"));
  const rowsPerPageFromUrl = validatePerPageQuery(urlParams.get("rowsPerPage"));
  const searchFromUrl = urlParams.get("search");

  const [contactsFilters, setContactsFilters] = useState<pageFilters>(
    initialPageFilterState(pageFromUrl, rowsPerPageFromUrl, searchFromUrl ?? "")
  );

  const hasActionPermission = subordinates?.includes(group?.userId);

  const hasContactDeletePermission = hasAccessTo(
    strings.contact,
    strings.deletePermission
  );

  const hasContactUpdatePermission = hasAccessTo(
    strings.contact,
    strings.updatePermission
  );

  const hasContactAddPermission = hasAccessTo(
    strings.contact,
    strings.addPermission
  );

  useEffect(() => {
    const { searchText } = contactsFilters;
    if (searchTerm.current !== null) searchTerm.current.value = searchText;

    getTableData();
  }, [contactsFilters]);

  const searchChangeHandler = (event: any) => {
    const searchText = event.target.value;
    setContactsFilters({
      ...contactsFilters,
      page: 1,
      searchText,
    });
  };

  const pageChangeHandler = (event: any, newPage: number) => {
    setContactsFilters({
      ...contactsFilters,
      page: newPage,
    });
  };

  const perPageRowsChangeHandler = (rowPerPage: number) => {
    setContactsFilters({
      ...contactsFilters,
      rowsPerPage: rowPerPage,
      page: 1,
    });
  };

  const getTableData = async () => {
    const { page, rowsPerPage, searchText } = contactsFilters;
    try {
      setIsLoading(true);
      const count = await getContactsCountBySegmentId(
        +id,
        strings.SEGMENT_GROUP,
        searchText.trim()
      );
      setCount(count);
      await getTableDataAccordingToPage(
        page,
        count,
        rowsPerPage,
        searchText.trim()
      );
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTableDataAccordingToPage = async (
    page: number,
    count: number,
    rowsPerPage: number,
    searchText: string
  ) => {
    try {
      let contactsRes: any[] = [];
      contactsRes = await getContactsBySegmentId(
        +id,
        strings.SEGMENT_GROUP,
        searchText.trim(),
        page,
        rowsPerPage
      );
      const tableData = convertDataToTableFormat(contactsRes);
      setContacts([...tableData]);
    } catch (error) {
      throw error;
    }
  };

  const convertDataToTableFormat = (data: any) => {
    return data.map((singleContact: any) => {
      const fullName = `${
        singleContact?.firstName ? singleContact?.firstName : ""
      } ${singleContact?.lastName ? singleContact?.lastName : ""}`;
      return {
        emailId: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {singleContact.emailId}
          </Typography>
        ),
        name: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {isTruthy(fullName) ? fullName : "N/A"}
          </Typography>
        ),
        status: (
          <Typography
            sx={{
              color: getColors[singleContact.status],
              display: "flex",
              alignItems: "center",
            }}
            variant="subtitle2"
          >
            <Box
              sx={{
                backgroundColor: getColors[singleContact.status],
              }}
            />
            {getGroupStatus(singleContact.status)}
          </Typography>
        ),
        createdOn: (
          <Typography sx={{ color: "#475569" }} variant="subtitle2">
            {convertESTtoUserLocalDateAndTime(
              singleContact.createdOn,
              "MMMM D, YYYY [at] h:mm A"
            )}
          </Typography>
        ),
        remove: subordinates.includes(singleContact.userId) &&
          hasContactDeletePermission && (
            <CustomInfoTooltip
              placement="bottom"
              infoMessage={"Remove contact from this group"}
            >
              <IconButton
                size="small"
                sx={{
                  color: "#fff",
                  padding: 0,
                  cursor: "pointer",
                }}
                onClick={(e: any) => {
                  e.stopPropagation();
                  setSelectedSingleContact(singleContact);
                  setIsDeleteSingleContactModal(true);
                }}
              >
                <img src={remove_icon} width="16px" alt="remove_icon" />
              </IconButton>
            </CustomInfoTooltip>
          ),
      };
    });
  };

  const handleExportContact = async () => {
    try {
      const res = await exportGroup(id);
      openSuccessNotification(res.message);
    } catch (error: any) {
      setIsLoading(false);
      handleErrorMessage(error);
    }
  };

  const deleteGroup = async (groupId: any) => {
    try {
      setIsApiLoading(true);
      const response = await deleteContactGroup(groupId);
      openSuccessNotification(response.message);
      setIsDeleteGroupModal(false);
      setIsApiLoading(false);
      history.goBack();
    } catch (error: any) {
      handleErrorMessage(error);
    } finally {
      setIsDeleteGroupModal(false);
      setIsLoading(false);
    }
  };

  const singleDeleteContact = async (signleContactId: any, groupId: any) => {
    try {
      setIsApiLoading(true);
      const response = await deleteContactSingleGroup(signleContactId, groupId);
      openSuccessNotification(response.message);
      setIsDeleteSingleContactModal(false);
      setIsApiLoading(false);
      setContactsFilters({
        ...contactsFilters,
        rowsPerPage: 10,
        page: 1,
      });
    } catch (error: any) {
      setIsApiLoading(false);
      handleErrorMessage(error);
    } finally {
      setIsDeleteSingleContactModal(false);
      setIsApiLoading(false);
    }
  };

  const getFilters = () => {
    return (
      <CenteredCardLayout removeLastChildBottomPadding={true}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <TextField
            id="contacts_search_box"
            placeholder="Search contacts in this group"
            variant="outlined"
            sx={{
              flex: 1,
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "none !important",
              },
              "&.MuiOutlinedInput-root.Mui-focused": {
                border: "none !important",
              },
            }}
            onChange={debounceEventHandler(
              searchChangeHandler,
              strings.SEARCH_TIME_OUT
            )}
            inputRef={searchTerm}
            InputProps={{
              startAdornment: (
                <Stack mr={1}>
                  <img src={SearchIcon} width="30px" height="20px" />
                </Stack>
              ),
            }}
          />
          {hasActionPermission && (
            <Stack direction="row" alignItems="center">
              <Button
                id="export_csv_btn"
                variant="text"
                size="small"
                startIcon={
                  <Stack>
                    <img src={ExportIcon} alt="Export CSV" />
                  </Stack>
                }
                sx={{
                  "& .MuiButton-startIcon": {
                    marginRight: "2px",
                  },
                }}
                disabled={isLoading}
                onClick={() => handleExportContact()}
              >
                Export Contacts
              </Button>

              {hasContactDeletePermission && (
                <Button
                  id="delete_grp_btn"
                  variant="text"
                  size="small"
                  startIcon={
                    <Stack>
                      <img src={DeleteIcon} alt="Delete Group" />
                    </Stack>
                  }
                  sx={{
                    alignItems: "flex-start",
                    "& .MuiButton-startIcon": {
                      marginRight: "4px",
                    },
                  }}
                  disabled={isLoading}
                  onClick={() => setIsDeleteGroupModal(true)}
                >
                  Delete Group
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </CenteredCardLayout>
    );
  };

  const getContactPageHeader = () => {
    return (
      <Stack direction="column" spacing={3}>
        <Stack direction="row" alignItems="center">
          <Link
            color="text.primary"
            href={viewpaths.groupsViewPath}
            sx={{
              alignItems: "center",
              display: "inline-flex",
            }}
            onClick={(e) => {
              e.preventDefault();
              history.goBack();
            }}
            underline="hover"
            component={RouterLink}
          >
            <KeyboardBackspace
              htmlColor="#475569"
              sx={{ cursor: "pointer", pr: 1 }}
            />
            <Typography variant="body1" color="text.primary" fontWeight={700}>
              Groups
            </Typography>
          </Link>
        </Stack>

        <Stack
          direction={{
            xs: "column",
            sm: "row",
          }}
          justifyContent="space-between"
          spacing={4}
        >
          <Stack alignItems="center" direction="row" spacing={2}>
            <Stack direction="column" spacing={1}>
              {isLoading ? (
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={25}
                  width={190}
                />
              ) : (
                <Stack direction="row" spacing={1} alignItems={"center"}>
                  <Typography
                    color="text.primary"
                    variant="h4"
                    sx={{ textTransform: "capitalize" }}
                  >
                    {group?.name}
                  </Typography>
                  {group?.status !== -1 && (
                    <Typography color="text.primary" variant="subtitle2">
                      <FiberManualRecordRoundedIcon
                        htmlColor="#475569"
                        sx={{ fontSize: "8px" }}
                      />{" "}
                      {getGroupStatus(group?.status)}
                    </Typography>
                  )}
                </Stack>
              )}
              {isLoading ? (
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={25}
                  width={120}
                />
              ) : (
                <Typography color="text.primary" variant="subtitle2">
                  <span
                    style={{
                      color: "text.primary",
                      fontWeight: 700,
                    }}
                  >
                    {getFormattedStatsCount(count ?? 0)}
                  </span>{" "}
                  {count! > 1 ? "Contacts" : "Contact"} added in this group
                </Typography>
              )}
            </Stack>
          </Stack>
          {hasActionPermission && (
            <Stack alignItems="center" direction="row" spacing={2}>
              {isLoading ? (
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={25}
                  width={120}
                />
              ) : (
                <>
                  {hasContactAddPermission && (
                    <Button
                      id="save_btn"
                      variant="contained"
                      onClick={() => setAddContactsModal(true)}
                    >
                      Add Contact
                    </Button>
                  )}
                </>
              )}
              {isLoading ? (
                <Skeleton
                  animation="wave"
                  variant="text"
                  height={25}
                  width={120}
                />
              ) : (
                <>
                  {hasContactUpdatePermission && (
                    <Button
                      id="save_btn"
                      variant="contained"
                      onClick={() => setIsUpdateGroupModal(true)}
                    >
                      Update
                    </Button>
                  )}
                </>
              )}
            </Stack>
          )}
        </Stack>
      </Stack>
    );
  };

  const getAddContactsModal = () => {
    return (
      <AddContactsModal
        handleDialogClose={() => setAddContactsModal(false)}
        group={group}
        isVisible={addContactsModal}
        contactsFilters={contactsFilters}
        setContactsFilters={setContactsFilters}
      />
    );
  };

  const getViewContactPage = () => {
    return (
      <Stack direction="column" spacing={2}>
        {getContactPageHeader()}
        {getFilters()}
        <CenteredCardLayout removeLastChildBottomPadding={true}>
          <Box>
            <CustomTable
              headers={contactGroupTableHeader}
              rows={contacts}
              page={contactsFilters.page}
              rowsPerPage={contactsFilters.rowsPerPage}
              onRowsPerPageChange={perPageRowsChangeHandler}
              onPageChange={pageChangeHandler}
              count={count}
              showJumpToPageNumber={false}
              handleOnChangePageInput={pageChangeHandler}
              showPagination={true}
              isLoading={isLoading}
            />
          </Box>
        </CenteredCardLayout>
      </Stack>
    );
  };

  return (
    <CenteredAuthenticatedLayout>
      {getViewContactPage()}
      {isDeleteGroupModal && (
        <ConfirmationModal
          isVisible={isDeleteGroupModal}
          handleDialogClose={() => {
            setIsDeleteGroupModal(false);
          }}
          title="Please Confirm"
          subtitle2={
            <Typography>
              {`${"Do you really want to remove "}`}
              <span style={{ fontWeight: 700 }}>{group?.name}?</span>
            </Typography>
          }
          btn1Title="Delete"
          btn1Variant="outlined"
          btn2Title="Cancel"
          btn2Variant="contained"
          btn1OnClick={() => deleteGroup(group?.id)}
          btn2OnClick={() => {
            setIsDeleteGroupModal(false);
          }}
        />
      )}
      {isDeleteSingleContactModal && (
        <ConfirmationModal
          isVisible={isDeleteSingleContactModal}
          handleDialogClose={() => {
            setIsDeleteSingleContactModal(false);
          }}
          title="Please Confirm"
          subtitle2={
            <Typography>
              {`${"Do you really want to remove "}`}
              <span style={{ fontWeight: 700 }}>
                {selectedSingleContact?.emailId}?
              </span>
            </Typography>
          }
          btn1Title="Delete"
          btn1Variant="outlined"
          btn2Title="Cancel"
          btn2Variant="contained"
          btn1OnClick={() =>
            singleDeleteContact(selectedSingleContact?.id, group?.id)
          }
          btn2OnClick={() => {
            setIsDeleteSingleContactModal(false);
          }}
        />
      )}
      {isUpdateGroupModal && (
        <UpdateGroupModal
          isVisible={isUpdateGroupModal}
          handleDialogClose={() => setIsUpdateGroupModal(false)}
          group={group}
          getActiveData={getTableData}
        />
      )}
      {addContactsModal && getAddContactsModal()}
      <CustomLoader isLoading={isApiLoading} />
    </CenteredAuthenticatedLayout>
  );
};

export default ViewGroup;
